/* ============================================================
   KAFIE 1901 - HERO REEL DRIVER  (session 33, amended session 36)

   Continuous drift, seamless loop, drag to scrub, videos loaded
   and played only while they are actually on screen.

   *** s36: HOVER NO LONGER PAUSES THE DRIFT. ***
   Danny: "The image carrousel should be continuously scrolling in
   the hero part. Whenever i hover, it stops, and it should not be
   like that." The mouseenter/mouseleave and focusin/focusout pause
   handlers were removed. The ONLY things that still stop the strip
   are: an active drag or touch (the user physically has hold of
   it), a HORIZONTAL wheel/trackpad swipe, a hidden tab, and
   prefers-reduced-motion. A vertical wheel over the reel is now
   ignored on purpose - the page scrolls under the pointer all the
   time and pausing on that reads to the user as "hover stops it",
   which is the exact bug being fixed here. If you re-add any
   pointer-proximity pause, you are undoing a direct request.

   WHY scrollLeft AND NOT A CSS ANIMATION
   A translateX keyframe cannot be dragged, cannot be swiped, and
   fights any manual scroll. Driving the real scroll container
   means touch, trackpad and mouse-drag all work with no extra code
   and the drift is just "keep adding to scrollLeft".

   WHY THE CONTENT IS CLONED ONCE
   The loop is done by jumping back exactly one copy-width when the
   drift passes it. Because the second copy is pixel-identical and
   sits immediately after the first, that jump is invisible. This
   is why measure() must add the flex gap to every item - miss it
   and the strip visibly stutters once per lap. Verified in the
   container's Playwright: the rendered item boxes at scrollLeft X
   and at X + half are byte-identical at X = 120 and X = 3000.

   VIDEO: src is NOT in the markup. It is in data-src and is only
   assigned the first time the element comes near the viewport, so
   a 14-item strip costs one poster JPEG each at load and nothing
   else. Clones share the same lazy path.
   ============================================================ */
(function () {
  var SPEED = 0.45;          /* px per frame ~ 27px/s at 60fps */
  var RESUME_AFTER = 1200;   /* ms of stillness before drift resumes after a drag/swipe */
  var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(reel) {
    /* shopify:section:load fires on every theme-editor edit. Without this
       guard the strip is cloned again on each one and half doubles. */
    if (reel.dataset.reelOn) return;
    reel.dataset.reelOn = '1';
    var vp = reel.querySelector('.rl-vp');
    var track = reel.querySelector('.rl-track');
    if (!vp || !track) return;

    var originals = Array.prototype.slice.call(track.children);
    if (!originals.length) return;

    /* --- one identical copy, for the seamless wrap --- */
    originals.forEach(function (n) {
      var c = n.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      c.setAttribute('tabindex', '-1');
      track.appendChild(c);
    });

    var half = 0;
    function measure() {
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
      var w = 0;
      originals.forEach(function (n) { w += n.getBoundingClientRect().width + gap; });
      half = w;
    }
    measure();
    if (window.ResizeObserver) new ResizeObserver(measure).observe(track);
    window.addEventListener('resize', measure);

    /* --- lazy video: assign src and play only while on screen --- */
    var vids = track.querySelectorAll('video[data-src]');
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var v = e.target;
          if (e.isIntersecting) {
            if (!v.getAttribute('src')) v.setAttribute('src', v.dataset.src);
            if (!calm) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { root: vp, rootMargin: '150px 400px', threshold: 0.01 });
      Array.prototype.forEach.call(vids, function (v) { io.observe(v); });
    } else {
      Array.prototype.forEach.call(vids, function (v) { v.setAttribute('src', v.dataset.src); });
    }

    /* --- the drift --- */
    var pos = 0, paused = false, idleTimer = null, dragging = false;

    function hold() {
      paused = true;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () { paused = false; }, RESUME_AFTER);
    }

    /* s36: NO mouseenter / mouseleave / focusin / focusout pause. Hovering
       must not stop the strip. Only a real gesture does. */

    /* A horizontal trackpad swipe is a deliberate scrub of THIS strip, so
       it holds. A vertical wheel is the page scrolling past under the
       pointer, so it is ignored. */
    vp.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) hold();
    }, { passive: true });

    vp.addEventListener('touchstart', function () { paused = true; clearTimeout(idleTimer); }, { passive: true });
    vp.addEventListener('touchend', hold, { passive: true });
    vp.addEventListener('touchcancel', hold, { passive: true });

    /* --- mouse drag to scrub --- */
    var startX = 0, startScroll = 0, moved = 0;
    vp.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      dragging = true; moved = 0; paused = true; clearTimeout(idleTimer);
      startX = e.clientX; startScroll = vp.scrollLeft;
      vp.classList.add('is-drag');
      vp.setPointerCapture(e.pointerId);
    });
    vp.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > moved) moved = Math.abs(dx);
      vp.scrollLeft = startScroll - dx;
      if (moved > 6) vp.classList.add('was-drag');
    });
    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      vp.classList.remove('is-drag');
      try { vp.releasePointerCapture(e.pointerId); } catch (err) {}
      setTimeout(function () { vp.classList.remove('was-drag'); }, 0);
      hold();
    }
    vp.addEventListener('pointerup', endDrag);
    vp.addEventListener('pointercancel', endDrag);

    /* POS IS AN ACCUMULATOR AND MUST NOT BE RE-READ FROM scrollLeft
       WHILE DRIFTING. Chrome snaps scrollLeft to whole device pixels,
       so `pos = vp.scrollLeft + SPEED` with a sub-pixel SPEED reads
       back the same integer every frame and the strip never moves at
       all. That was the first version of this file, and it looked
       exactly like a broken carousel. Accumulate in JS, write to the
       DOM. Measured after the fix: 27px/s, steady. */
    function frame() {
      if (half > 0) {
        if (!paused && !calm && !document.hidden) {
          pos += SPEED;
          if (pos >= half) pos -= half;
          vp.scrollLeft = pos;
        } else {
          /* the user has it: keep them inside the loop, and stay in
             sync so the drift resumes from where they left it */
          var s = vp.scrollLeft;
          if (s >= half) { s -= half; vp.scrollLeft = s; }
          else if (s <= 0 && dragging) { s += half; vp.scrollLeft = s; }
          pos = s;
        }
      }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-reel]'), init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
